export type ApiErrorInfo = {
  message: string
  status?: number
  code?: string
  isBanner?: boolean
}

// Shown for every 5xx response (500/502/503/504/etc). These come from either
// our own backend's catch-all handler OR an upstream gateway (Apigee/Azure
// App Gateway) that timed out before our backend ever responded — in the
// gateway case the response body is often a raw HTML error page, not JSON,
// so 5xx never falls back to whatever text happens to be in the response
// body the way 400/401/403/404 do. One consistent, safe message instead.
export const BACKEND_UNAVAILABLE_MESSAGE =
  'Could not process the time off request. If the problem continues after re-logging in, email the National TORT Support team at NAT-TORT@kp.org with your name, employee ID, NUID, computer screenshots, and a description of the issue.'

// Normalize various error shapes (Axios, Fetch) into a friendly message
export const extractApiErrorMessage = (
  error: any,
  defaultMessage = 'Unexpected error occurred'
): string => {
  const status: number | undefined = error?.response?.status
  const statusText: string | undefined = error?.response?.statusText

  // Extract message from various response formats
  let dataMessage: string | undefined
  if (typeof error?.response?.data === 'string') {
    dataMessage = error?.response?.data
  } else if (error?.response?.data?.message) {
    dataMessage = error?.response?.data?.message
  } else if (error?.response?.data?.error) {
    dataMessage = error?.response?.data?.error
  } else if (error?.response?.data?.detail) {
    dataMessage = error?.response?.data?.detail
  }

  let message: string

  // Friendly overrides by status
  switch (status) {
    case 400:
      message =
        dataMessage || 'Your request could not be completed. Please try again.'
      break

    case 401:
      message = dataMessage || 'Your session has expired. Please log in again.'
      break

    case 403:
      message =
        dataMessage || 'You do not have permission to perform this action.'
      break

    case 404:
      message = dataMessage || 'The requested resource is not available.'
      break

    case 500:
    case 502:
    case 503:
    case 504:
      message = BACKEND_UNAVAILABLE_MESSAGE
      break

    default:
      // If we have any error data message, use it
      if (dataMessage) {
        message = dataMessage
      } else if (error?.message) {
        message = error?.message
      } else if (statusText) {
        message = statusText
      } else {
        message = defaultMessage
      }
  }

  return message
}

// Extract error object with severity flag for banner display
export const extractApiError = (error: any): ApiErrorInfo => {
  const status: number | undefined = error?.response?.status
  const code: string | undefined = error?.code
  const message = extractApiErrorMessage(error)

  // Determine if error should display as banner (severe errors)
  const isBanner = (status !== undefined && status >= 500) || code === 'ERR_NETWORK'

  return {
    message,
    status,
    code,
    isBanner,
  }
}
